// apps/web/src/pages/admin/reservations/ReservationRow.tsx — Phase 5 plan 05-04 (B4)
// + Quick 260803-ger.
//
// Single row for the Reservations page. Renders reservationNumber,
// time, guest + contact, party size, status tag, source tag, inline
// internalNote, edit + cancel actions.
//
// As of 260803-ger, this component is split: the card markup
// (number / time / contact / tags / party) is provided by
// TodayListView, and `ReservationRowActions` is the action slot
// (edit + cancel buttons) that the parent injects via TodayListView's
// `renderItem` callback. The `ReservationRow` export is kept for
// backward-compat callers and the legacy test surface; it composes
// the card + actions.

import { Link } from "react-router-dom";
import { format } from "date-fns";
import { useTranslation } from "react-i18next";
import { StatusTag, reservationRowClass } from "../shared/StatusTag";
import { SourceTag } from "../shared/SourceTag";
import type { ReservationStatusValue } from "../shared/StatusTag";
import type { ReservationSourceValue } from "../shared/SourceTag";

export interface ReservationRowItem {
  id: string;
  reservationNumber: string;
  status: ReservationStatusValue;
  source: ReservationSourceValue;
  startsAt: string;
  durationMinutes: number;
  partySize: number;
  guestName: string;
  guestEmail: string | null;
  guestPhone: string | null;
  internalNote: string | null;
  slotDefinitionId: string;
  serviceDate: string;
}

export interface ReservationRowReturnFilter {
  date: string;
  search: string;
  status: string[];
  source: string[];
}

function buildEditPath(
  item: ReservationRowItem,
  returnFilter: ReservationRowReturnFilter | undefined,
): string {
  if (!returnFilter) return `/admin/reservierungen/${item.id}`;
  const params = new URLSearchParams({
    from_date: returnFilter.date,
    from_search: returnFilter.search,
    from_status: returnFilter.status.join(","),
    from_source: returnFilter.source.join(","),
  });
  return `/admin/reservierungen/${item.id}?${params.toString()}`;
}

export function ReservationRowActions({
  item,
  onCancel,
  onConfirm,
  onMarkArrived,
  onMarkNoShow,
  isCancelling,
  isConfirming,
  isMarkingArrived,
  isMarkingNoShow,
  returnFilter,
}: {
  item: ReservationRowItem;
  onCancel: (id: string) => void;
  onConfirm: (id: string) => void;
  onMarkArrived: (id: string) => void;
  onMarkNoShow: (id: string) => void;
  isCancelling: boolean;
  isConfirming: boolean;
  isMarkingArrived: boolean;
  isMarkingNoShow: boolean;
  returnFilter?: ReservationRowReturnFilter;
}) {
  const { t } = useTranslation();
  const editPath = buildEditPath(item, returnFilter);
  // Quick 260831-q3h: inline status-transition buttons per the 5-state
  // machine (D-06 + D-07). Terminal states (NO_SHOW, STORNIERT) only
  // show "Bearbeiten" — the server rejects reverse transitions, so
  // offering them on the client would just produce a 422 error toast.
  const showConfirm = item.status === "ANGEFRAGT";
  const showArrived = item.status === "BESTAETIGT";
  // NO_SHOW is only valid from BESTAETIGT — the server rejects
  // ANGEKOMMEN → NO_SHOW (Phase 5 D-07: arrived is a forward-only
  // terminal; once the guest is in the seat the only legal exits
  // are cancel or stay).
  const showNoShow = item.status === "BESTAETIGT";
  const showCancel =
    item.status === "ANGEFRAGT" ||
    item.status === "BESTAETIGT" ||
    item.status === "ANGEKOMMEN";

  return (
    <div
      className="flex flex-wrap gap-2"
      data-testid={`reservation-row-actions-${item.id}`}
    >
      <Link
        to={editPath}
        className="btn btn-outline"
        data-testid={`reservation-edit-${item.id}`}
      >
        {t("admin.reservations.row.edit")}
      </Link>
      {showConfirm && (
        <button
          type="button"
          onClick={() => onConfirm(item.id)}
          disabled={isConfirming}
          className="btn btn-primary"
          data-testid={`reservation-confirm-${item.id}`}
        >
          {t("admin.reservations.row.confirm")}
        </button>
      )}
      {showArrived && (
        <button
          type="button"
          onClick={() => onMarkArrived(item.id)}
          disabled={isMarkingArrived}
          className="btn btn-outline"
          data-testid={`reservation-mark-arrived-${item.id}`}
        >
          {t("admin.reservations.row.markArrived")}
        </button>
      )}
      {showNoShow && (
        <button
          type="button"
          onClick={() => onMarkNoShow(item.id)}
          disabled={isMarkingNoShow}
          className="btn btn-outline"
          data-testid={`reservation-mark-no-show-${item.id}`}
        >
          {t("admin.reservations.row.markNoShow")}
        </button>
      )}
      {showCancel && (
        <button
          type="button"
          onClick={() => onCancel(item.id)}
          disabled={isCancelling}
          className="btn btn-outline"
          data-testid={`reservation-cancel-${item.id}`}
        >
          {t("admin.reservations.row.cancel")}
        </button>
      )}
    </div>
  );
}

export function ReservationRow({
  item,
  onCancel,
  onConfirm,
  onMarkArrived,
  onMarkNoShow,
  isCancelling,
  isConfirming,
  isMarkingArrived,
  isMarkingNoShow,
  returnFilter,
}: {
  item: ReservationRowItem;
  onCancel: (id: string) => void;
  onConfirm: (id: string) => void;
  onMarkArrived: (id: string) => void;
  onMarkNoShow: (id: string) => void;
  isCancelling: boolean;
  isConfirming: boolean;
  isMarkingArrived: boolean;
  isMarkingNoShow: boolean;
  returnFilter?: ReservationRowReturnFilter;
}) {
  const { t } = useTranslation();
  return (
    <li
      className={`card p-4 flex flex-col gap-2 ${reservationRowClass(item.status)}`}
      data-testid={`reservation-row-${item.id}`}
    >
      <div className="flex items-center justify-between">
        <span className="font-heading text-lg">
          {item.reservationNumber} · {format(new Date(item.startsAt), "HH:mm")}
        </span>
        <div className="flex gap-2">
          <StatusTag status={item.status} />
          <SourceTag source={item.source} />
        </div>
      </div>
      <div className="text-sm">
        <span className="font-medium">{item.guestName}</span>
        {/* Render email and phone independently — the prior
            `??` collapse hid the phone whenever email was set,
            which blocks the staff "call the guest" workflow
            for the most common case (online booking). */}
        {item.guestEmail && (
          <span className="text-text/60"> · {item.guestEmail}</span>
        )}
        {item.guestPhone && (
          <span className="text-text/60"> · {item.guestPhone}</span>
        )}
        {!item.guestEmail && !item.guestPhone && (
          <span className="text-text/60"> · —</span>
        )}
        <span className="text-text/60"> · {item.partySize} Pers.</span>
      </div>
      {item.internalNote && (
        <p className="text-xs text-text/60 italic">
          <span className="font-medium">
            {t("admin.reservations.row.internalNoteLabel")}:
          </span>{" "}
          {item.internalNote}
        </p>
      )}
      <ReservationRowActions
        item={item}
        onCancel={onCancel}
        onConfirm={onConfirm}
        onMarkArrived={onMarkArrived}
        onMarkNoShow={onMarkNoShow}
        isCancelling={isCancelling}
        isConfirming={isConfirming}
        isMarkingArrived={isMarkingArrived}
        isMarkingNoShow={isMarkingNoShow}
        {...(returnFilter ? { returnFilter } : {})}
      />
    </li>
  );
}
